import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { memo, useEffect, useState } from "react";
import { DateRangePicker } from "./date-picker/date-picker-menu";

export interface RedoDateRangePickerDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateRange: (
    dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  allowNoDateOption?: boolean;
  style?: React.CSSProperties;
  popperModifiers?: any[];
}

export const RedoDateRangePickerDropdown = memo(
  function RedoDateRangePickerDropdown({
    anchorEl,
    open,
    onClose,
    dateRange,
    setDateRange,
    minDate,
    maxDate,
    allowNoDateOption = false,
    style,
    popperModifiers,
  }: RedoDateRangePickerDropdownProps) {
    const [localStartDate, setLocalStartDate] =
      useState<Temporal.PlainDate | null>(null);

    const [localEndDate, setLocalEndDate] = useState<Temporal.PlainDate | null>(
      null,
    );

    const handleSetCustomDate = useHandler(
      (start: Temporal.PlainDate | null, end: Temporal.PlainDate | null) => {
        setLocalStartDate(start);
        setLocalEndDate(end);
      },
    );

    useEffect(() => {
      setLocalStartDate(dateRange ? dateRange[0] : null);
      setLocalEndDate(dateRange ? dateRange[1] : null);
    }, [dateRange]);

    const handleApply = useHandler(() => {
      if (localStartDate && localEndDate) {
        const newArray: [Temporal.PlainDate, Temporal.PlainDate] = [
          localStartDate,
          localEndDate,
        ];

        setDateRange(newArray);
      } else {
        setDateRange(undefined);
      }
      onClose();
    });

    const defaultModifiers = [
      { name: "offset", options: { offset: [0, 8] } },
      // Keep the preferred "below the anchor" placement, but when the picker
      // would run off the screen, pan it back into the viewport (on both axes)
      // with a little padding — even if that means covering the anchor button.
      // tether:false lets the popper detach from the reference so it can slide
      // fully on-screen; rootBoundary:"viewport" measures against the window
      // rather than a clipping scroll ancestor. We intentionally do NOT include
      // `flip`: on a constrained screen neither side fits, so flipping above is
      // no better — panning into view is what we want.
      {
        name: "preventOverflow",
        options: {
          padding: 8,
          tether: false,
          mainAxis: true,
          altAxis: true,
          rootBoundary: "viewport",
        },
      },
    ];

    // Merge caller-supplied modifiers over the defaults by name so that
    // viewport-keeping behavior (preventOverflow) is always preserved. A caller
    // passing only an `offset` modifier should not silently drop the overflow
    // handling and end up positioned off-screen.
    const mergedModifiers = popperModifiers
      ? [
          ...defaultModifiers.filter(
            (d) => !popperModifiers.some((m) => m.name === d.name),
          ),
          ...popperModifiers,
        ]
      : defaultModifiers;

    return (
      <ClickAwayListener onClickAway={onClose}>
        <Popper
          anchorEl={anchorEl}
          modifiers={mergedModifiers}
          open={open}
          placement="bottom-start"
          style={style}
        >
          <DateRangePicker
            allowNoDateOption={allowNoDateOption}
            endDate={localEndDate}
            maxDate={maxDate}
            minDate={minDate}
            onApply={handleApply}
            onCancel={onClose}
            onRangeChange={handleSetCustomDate}
            startDate={localStartDate}
            variant="range"
          />
        </Popper>
      </ClickAwayListener>
    );
  },
);
